import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase, toSnakeCase } from '../lib/typeHelper'

// Mirror of the backend `Document.DocumentType` (insure/models/document.py).
// Keep in sync so every type the API can return has an explicit UI label.
export enum DocumentType {
  CDL = 'CDL',
  COI = 'COI',
  COI_WITH_HOLDER = 'COI_WITH_HOLDER',
  DRIVER_IMPORT = 'DRIVER_IMPORT',
  TRUCK_IMPORT = 'TRUCK_IMPORT',
  TRAILER_IMPORT = 'TRAILER_IMPORT',
  CERTIFICATE_HOLDER_IMPORT = 'CERTIFICATE_HOLDER_IMPORT',
  // Legacy CSV_* import types, retained for historical rows.
  CSV_DRIVER_IMPORT = 'CSV_DRIVER_IMPORT',
  CSV_TRUCK_IMPORT = 'CSV_TRUCK_IMPORT',
  CSV_TRAILER_IMPORT = 'CSV_TRAILER_IMPORT',
  CSV_CERTIFICATE_HOLDER_IMPORT = 'CSV_CERTIFICATE_HOLDER_IMPORT',
  SALES_LEAD_CSV_IMPORT = 'SALES_LEAD_CSV_IMPORT',
  LOSS_RUN = 'LOSS_RUN',
  MVR = 'MVR',
  IFTA = 'IFTA',
  BOR_AUTHORIZATION = 'BOR_AUTHORIZATION',
  CANAL_BOR_AUTHORIZATION = 'CANAL_BOR_AUTHORIZATION',
  ROCKLAKE_PAYMENT_AUTHORIZATION = 'ROCKLAKE_PAYMENT_AUTHORIZATION',
  ROCKLAKE_PAYMENT_AUTHORIZATION_AUDIT_LOG = 'ROCKLAKE_PAYMENT_AUTHORIZATION_AUDIT_LOG',
  ROCKLAKE_CREDIT_CARD_AUTHORIZATION = 'ROCKLAKE_CREDIT_CARD_AUTHORIZATION',
  ROCKLAKE_CREDIT_CARD_AUTHORIZATION_AUDIT_LOG = 'ROCKLAKE_CREDIT_CARD_AUTHORIZATION_AUDIT_LOG',
  EMAIL_ATTACHMENT = 'EMAIL_ATTACHMENT',
  POLICY_DOCUMENT = 'POLICY_DOCUMENT',
  POLICY_PARSE_DOCUMENT = 'POLICY_PARSE_DOCUMENT',
  QUOTE_DOCUMENT = 'QUOTE_DOCUMENT',
  CHAT_ATTACHMENT = 'CHAT_ATTACHMENT',
  CALL_RECORDING = 'CALL_RECORDING',
  SMS_ATTACHMENT = 'SMS_ATTACHMENT',
  CAB_REPORT = 'CAB_REPORT',
  CLAIM_PHOTO = 'CLAIM_PHOTO',
  FILLED_FORM = 'FILLED_FORM',
  CARRIER_SUBMISSION_SCREENSHOTS = 'CARRIER_SUBMISSION_SCREENSHOTS',
  ATTESTATION = 'ATTESTATION',
  OTHER = 'OTHER',
}

export const documentTypeLabels: Record<DocumentType, string> = {
  [DocumentType.CDL]: 'CDL',
  [DocumentType.COI]: 'COI',
  [DocumentType.COI_WITH_HOLDER]: 'COI Holder',
  [DocumentType.DRIVER_IMPORT]: 'Driver Import',
  [DocumentType.TRUCK_IMPORT]: 'Truck Import',
  [DocumentType.TRAILER_IMPORT]: 'Trailer Import',
  [DocumentType.CERTIFICATE_HOLDER_IMPORT]: 'Certificate Holder Import',
  [DocumentType.CSV_DRIVER_IMPORT]: 'Driver Import',
  [DocumentType.CSV_TRUCK_IMPORT]: 'Truck Import',
  [DocumentType.CSV_TRAILER_IMPORT]: 'Trailer Import',
  [DocumentType.CSV_CERTIFICATE_HOLDER_IMPORT]: 'Certificate Holder Import',
  [DocumentType.SALES_LEAD_CSV_IMPORT]: 'Sales Lead Import',
  [DocumentType.LOSS_RUN]: 'Loss Run',
  [DocumentType.MVR]: 'MVR',
  [DocumentType.IFTA]: 'IFTA',
  [DocumentType.BOR_AUTHORIZATION]: 'BOR Authorization',
  [DocumentType.CANAL_BOR_AUTHORIZATION]: 'Canal BOR Authorization',
  [DocumentType.ROCKLAKE_PAYMENT_AUTHORIZATION]: 'Rocklake Payment Authorization',
  [DocumentType.ROCKLAKE_PAYMENT_AUTHORIZATION_AUDIT_LOG]: 'Rocklake Payment Authorization Audit Log',
  [DocumentType.ROCKLAKE_CREDIT_CARD_AUTHORIZATION]: 'Rocklake Credit Card Authorization',
  [DocumentType.ROCKLAKE_CREDIT_CARD_AUTHORIZATION_AUDIT_LOG]: 'Rocklake Credit Card Authorization Audit Log',
  [DocumentType.EMAIL_ATTACHMENT]: 'Email Attachment',
  [DocumentType.POLICY_DOCUMENT]: 'Policy Document',
  [DocumentType.POLICY_PARSE_DOCUMENT]: 'Parsed Policy Document',
  [DocumentType.QUOTE_DOCUMENT]: 'Quote Document',
  [DocumentType.CHAT_ATTACHMENT]: 'Chat Attachment',
  [DocumentType.SMS_ATTACHMENT]: 'SMS Attachment',
  [DocumentType.CALL_RECORDING]: 'Call Recording',
  [DocumentType.CAB_REPORT]: 'CAB Report',
  [DocumentType.CLAIM_PHOTO]: 'Claim Photo',
  [DocumentType.FILLED_FORM]: 'Filled Form',
  [DocumentType.CARRIER_SUBMISSION_SCREENSHOTS]: 'Carrier Submission Screenshots',
  [DocumentType.ATTESTATION]: 'Attestation',
  [DocumentType.OTHER]: 'Other',
}

/** Human label for a document type, falling back to a title-cased version of the raw key. */
export function documentTypeLabel(type: string): string {
  // Cast widens the key type: the backend can send document types not in the enum.
  const known = (documentTypeLabels as Record<string, string | undefined>)[type]
  if (known !== undefined) return known
  return type
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export interface DocumentAttachment {
  id: string
  dateCreated: string
  dateUpdated: string
  attachmentType: string
  file: string
  fileName: string
  fileExtension: string
  fileSize: number
}

/** The parsed name and dates every document listing serves, from `ParsedDocumentFieldsMixin`. */
export interface ParsedDocumentFields {
  /** User-set name, else the parsed title, else the filename — resolved server-side. */
  displayName: string
  /** PENDING while the parser still has work to do; '' when it never will. */
  parseStatus: string
  dateEffective: string | null
  dateExpiration: string | null
}

export interface Document extends ParsedDocumentFields {
  id: string
  dateCreated: string
  dateUpdated: string
  dateLastUploaded: string
  documentType: DocumentType
  createdBy: string
  org: string | null
  relatedModel: string | null
  relatedModelId: string | null
  metadata: Record<string, unknown>
  attachments: DocumentAttachment[]
}

export const PARSE_PENDING = 'PENDING'

/** How often to re-ask while a parse is outstanding. An LLM read takes seconds. */
export const PARSE_POLL_MS = 4000

/** Takes the one field it reads, so a vault slot's documents qualify too. */
export function hasPendingParse(documents: { parseStatus: string }[]): boolean {
  return documents.some((document) => document.parseStatus === PARSE_PENDING)
}

function getOrgDocuments(orgId: string, documentTypes?: DocumentType[]): Promise<PaginatedResults<Document>> {
  return axios
    .get(`/api/v1/insure/organisations/${orgId}/documents/`, {
      params: documentTypes?.length ? { document_type: documentTypes.join(',') } : undefined,
    })
    .then((response) => toCamelCase(response.data) as PaginatedResults<Document>)
}

/** Pass `documentTypes` to filter server-side — otherwise the default 100-item page can hide older matches. */
export function useOrgDocuments(orgId: string | undefined, documentTypes?: DocumentType[]) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.ORG_DOCUMENTS, orgId, documentTypes],
    queryFn: () => getOrgDocuments(orgId!, documentTypes),
    enabled: !!orgId,
    refetchInterval: (query) => (hasPendingParse(query.state.data?.results ?? []) ? PARSE_POLL_MS : false),
  })
  return { documents: data?.results ?? [], count: data?.count ?? 0, isLoading }
}

export interface DocumentGroup {
  documentType: DocumentType
  count: number
  /** Populated when `count` is small enough to render inline; empty otherwise — fetch via `useOrgDocumentsByType` once expanded. */
  documents: Document[]
}

function getOrgDocumentGroups(orgId: string): Promise<DocumentGroup[]> {
  return axios
    .get(`/api/v1/insure/organisations/${orgId}/documents/grouped/`)
    .then((response) => toCamelCase(response.data) as DocumentGroup[])
}

/** One entry per document type present for this org, most-recently-active first — small types come with their documents inline, large types come as a count only. */
export function useOrgDocumentGroups(orgId: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.ORG_DOCUMENT_GROUPS, orgId],
    queryFn: () => getOrgDocumentGroups(orgId!),
    enabled: !!orgId,
    refetchInterval: (query) =>
      hasPendingParse((query.state.data ?? []).flatMap((group) => group.documents)) ? PARSE_POLL_MS : false,
  })
  return { groups: data ?? [], isLoading }
}

function getOrgDocumentsByType(orgId: string, documentType: DocumentType): Promise<Document[]> {
  return axios
    .get(`/api/v1/insure/organisations/${orgId}/documents/by_type/`, {
      params: { document_type: documentType },
    })
    .then((response) => toCamelCase(response.data) as Document[])
}

/** Every document of one type, unpaginated — for expanding a collapsed group (the plain list endpoint's fixed page size would truncate it). */
export function useOrgDocumentsByType(orgId: string | undefined, documentType: DocumentType) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.ORG_DOCUMENTS_BY_TYPE, orgId, documentType],
    queryFn: () => getOrgDocumentsByType(orgId!, documentType),
    enabled: !!orgId,
    refetchInterval: (query) => (hasPendingParse(query.state.data ?? []) ? PARSE_POLL_MS : false),
  })
  return { documents: data ?? [], isLoading }
}

// Document types a CAI admin can upload from the customer documents tab.
// Policy documents are uploaded from the Insurance tab (so we can parse + diff
// them against the policy on file) and COIs from the COI tab's certificate
// holder drawer, not from the generic documents dropdown.
export const UPLOADABLE_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.BOR_AUTHORIZATION,
  DocumentType.LOSS_RUN,
  DocumentType.MVR,
  DocumentType.IFTA,
  DocumentType.OTHER,
]

// Mirrors CUSTOMER_UPLOADABLE_DOCUMENT_TYPES in documents_view.py: every other
// type drives something (loss runs populate claims, imports rewrite the fleet).
const CUSTOMER_UPLOADABLE_DOCUMENT_TYPES: DocumentType[] = [DocumentType.OTHER]

/** The types this user may actually upload — offering more just earns a 403. */
export function uploadableDocumentTypes(isCaiAdmin: boolean): DocumentType[] {
  return isCaiAdmin ? UPLOADABLE_DOCUMENT_TYPES : CUSTOMER_UPLOADABLE_DOCUMENT_TYPES
}

export function uploadOrgDocument(orgId: string, file: File, documentType: DocumentType): Promise<Document> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('document_type', documentType)
  return axios
    .post(`/api/v1/insure/organisations/${orgId}/documents/`, formData)
    .then((response) => toCamelCase(response.data) as Document)
}

export interface LeadDocumentAttachment {
  id: string
  dateCreated: string
  attachmentType: string
  fileName: string
  fileExtension: string
  fileSize: number
  fileUrl: string | null
}

export interface LeadDocument extends ParsedDocumentFields {
  id: string
  dateCreated: string
  dateUpdated: string
  dateLastUploaded: string
  documentType: string
  relatedModel: string | null
  /** Null until the lead converts — renaming and the parse detail are org-scoped. */
  org: string | null
  attachments: LeadDocumentAttachment[]
}

function getLeadDocuments(leadId: string): Promise<LeadDocument[]> {
  return axios
    .get(`/api/v1/insure/internal/sales-leads/${leadId}/documents/`)
    .then((response) => toCamelCase(response.data) as LeadDocument[])
}

export function useLeadDocuments(leadId: string | undefined) {
  return useQuery({
    queryKey: [CacheKeys.LEAD_DOCUMENTS, leadId],
    queryFn: () => getLeadDocuments(leadId!),
    enabled: !!leadId,
    refetchInterval: (query) => (hasPendingParse(query.state.data ?? []) ? PARSE_POLL_MS : false),
  })
}

/** The minimum the rename dialog and the metadata drawer need off a document. */
export interface NamedDocument {
  id: string
  displayName: string
}

/** Every cache a rename or a refile changes — both document listings and the vault's. */
export function invalidateDocumentQueries(queryClient: QueryClient, orgId: string) {
  const orgScoped = [
    CacheKeys.ORG_DOCUMENTS,
    CacheKeys.ORG_DOCUMENT_GROUPS,
    CacheKeys.ORG_DOCUMENTS_BY_TYPE,
    CacheKeys.VAULT_TREE,
    CacheKeys.VAULT_FOLDER,
    CacheKeys.VAULT_FILE,
    CacheKeys.VAULT_TIMELINE,
  ]
  for (const key of orgScoped) {
    void queryClient.invalidateQueries({ queryKey: [key, orgId] })
  }
  // Lead documents are keyed by lead, so the org id cannot narrow this one.
  void queryClient.invalidateQueries({ queryKey: [CacheKeys.LEAD_DOCUMENTS] })
}

export function useUpdateDocument(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; folder?: string | null }) =>
      axios.patch(`/api/v1/insure/organisations/${orgId}/documents/${id}/`, input),
    onSuccess: () => invalidateDocumentQueries(queryClient, orgId),
  })
}

export interface DocumentSweepSummary {
  examined: number
  retyped: number
  linked: number
  retypedIds: string[]
  linkedIds: string[]
  queuedForParse: number
}

/** Staff only: re-file the org's parsed documents from what the parser read, and parse the rest. */
export function useSweepDocuments(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      axios
        .post(`/api/v1/insure/organisations/${orgId}/documents/sweep/`)
        .then((r) => toCamelCase(r.data) as DocumentSweepSummary),
    onSuccess: () => invalidateDocumentQueries(queryClient, orgId),
  })
}

/** Upload into the vault's `folder` when given; otherwise the document self-files by type. */
export function useUploadDocument(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      file,
      documentType,
      folder,
    }: {
      file: File
      documentType: string
      folder: string | null
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('document_type', documentType)
      if (folder) formData.append('folder', folder)
      return axios.post(`/api/v1/insure/organisations/${orgId}/documents/`, formData)
    },
    onSuccess: () => invalidateDocumentQueries(queryClient, orgId),
  })
}

export interface ParsedAttachment {
  id: string
  fileName: string
  fileSize: number | null
  file: string | null
  status: string
  error: string
  data: Record<string, unknown>
}

export interface DocumentParseDetail {
  id: string
  displayName: string
  documentType: string
  suggestedDocumentType: string | null
  status: string
  dateParsed: string
  error: string
  attempts: number
  data: Record<string, unknown>
  attachments: ParsedAttachment[]
}

export interface ParsedDates {
  dateEffective: string | null
  dateExpiration: string | null
  dateIssued: string | null
}

/** Corrects the dates the parser read off one file; the vault's slots follow at once. */
export function useSetParsedDates(orgId: string, documentId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ParsedDates & { attachmentId?: string }) =>
      axios
        .patch(`/api/v1/insure/organisations/${orgId}/documents/${documentId}/dates/`, toSnakeCase(payload))
        .then((r) => toCamelCase(r.data) as DocumentParseDetail),
    onSuccess: (detail) => {
      queryClient.setQueryData([CacheKeys.VAULT_DOCUMENT_PARSE, orgId, documentId], detail)
      invalidateDocumentQueries(queryClient, orgId)
    },
  })
}

/** Fetched only when the panel opens — the extraction is far bigger than the row. */
export function useDocumentParseDetail(orgId: string | null | undefined, documentId: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.VAULT_DOCUMENT_PARSE, orgId, documentId],
    queryFn: () =>
      axios
        .get(`/api/v1/insure/organisations/${orgId!}/documents/${documentId!}/parsed/`)
        .then((r) => toCamelCase(r.data) as DocumentParseDetail),
    enabled: !!orgId && !!documentId,
  })
  return { detail: data, isLoading }
}
